import { createEffect, createSignal } from "solid-js";
import { SessionState } from "../../types/SessionState";
import type { CaseState } from "../../types/caseState";
import {
  SUGGESTIONS_READY_SECONDS,
  SUGGESTIONS_READY_MIN_UTTERANCES,
  SUGGESTIONS_THROTTLE_MS,
  SUGGESTIONS_UTT_DELTA,
  SUGGESTIONS_FETCH_DEBOUNCE_MS,
  SUGGESTIONS_CASE_STATE_THROTTLE_MS,
  SUGGESTIONS_CASE_STATE_DEBOUNCE_MS,
} from "../../config/interviewConfig";

/**
 * Encapsulates the "when should we refresh suggestions" logic:
 * - Wait until some transcript exists and a minimum time elapses
 * - Throttle by utterance delta and elapsed time
 * - Debounce fetches to avoid burst calls
 */
export function useSuggestionsRefresh(input: {
  sessionState: () => SessionState;
  seconds: () => number;
  utterances: () => {
    speaker: number;
    text: string;
    translation?: string;
    timestamp?: string;
  }[];
  awaitingAnswer: () => boolean;
  contactType: () => string;
  caseState: () => CaseState;
  buildAnalysisTranscript: (uts: ReturnType<typeof input.utterances>) => string;
  fetchSuggestions: (
    transcript: string,
    state: CaseState,
    contactType: string,
  ) => void;
}) {
  const [readyForSuggestions, setReadyForSuggestions] = createSignal(false);

  let timer: ReturnType<typeof setTimeout>;
  let lastFetchAtMs = 0;
  let lastUtteranceCount = 0;

  createEffect(() => {
    if (readyForSuggestions()) return;
    if (input.sessionState() !== SessionState.Listening) return;
    if (
      input.utterances().length >= SUGGESTIONS_READY_MIN_UTTERANCES &&
      input.seconds() >= SUGGESTIONS_READY_SECONDS
    ) {
      setReadyForSuggestions(true);
    }
  });

  // Utterance-driven refresh
  createEffect(() => {
    if (!readyForSuggestions()) return;
    if (input.sessionState() !== SessionState.Listening) return;
    if (input.awaitingAnswer()) return;

    const now = Date.now();
    const uts = input.utterances();
    const added = uts.length - lastUtteranceCount;
    const elapsed = now - lastFetchAtMs;

    const shouldFetch =
      added >= SUGGESTIONS_UTT_DELTA || elapsed >= SUGGESTIONS_THROTTLE_MS;
    if (!shouldFetch) return;

    lastUtteranceCount = uts.length;
    lastFetchAtMs = now;

    clearTimeout(timer);
    timer = setTimeout(() => {
      const transcript = input.buildAnalysisTranscript(uts);
      input.fetchSuggestions(
        transcript,
        input.caseState(),
        input.contactType(),
      );
    }, SUGGESTIONS_FETCH_DEBOUNCE_MS);
  });

  // Case-state driven refresh (form edits)
  createEffect(() => {
    if (!readyForSuggestions()) return;
    if (input.sessionState() !== SessionState.Listening) return;
    if (input.awaitingAnswer()) return;

    const now = Date.now();
    const elapsed = now - lastFetchAtMs;
    if (elapsed < SUGGESTIONS_CASE_STATE_THROTTLE_MS) return;

    clearTimeout(timer);
    timer = setTimeout(() => {
      const transcript = input.buildAnalysisTranscript(input.utterances());
      lastFetchAtMs = Date.now();
      input.fetchSuggestions(
        transcript,
        input.caseState(),
        input.contactType(),
      );
    }, SUGGESTIONS_CASE_STATE_DEBOUNCE_MS);
  });

  return { readyForSuggestions };
}
